'use client'

import React from 'react'

import { DEFAULT_PALETTE, isPaletteId, PALETTES, PALETTE_STORAGE_KEY, type PaletteId } from '@/palettes'
import { cn } from '@/utilities/cn'

/**
 * Переключатель палитры — чтобы посмотреть варианты прямо на живом сайте,
 * а не на картинках. Включается переменной NEXT_PUBLIC_PALETTE_PREVIEW,
 * поэтому на проде его нет.
 *
 * Выбор хранится в localStorage и дублируется в адресе (?palette=…), чтобы
 * ссылкой можно было поделиться.
 */
export function PaletteSwitcher() {
  const [open, setOpen] = React.useState(false)
  const [current, setCurrent] = React.useState<PaletteId>(DEFAULT_PALETTE)

  // стартовое значение ставит скрипт в <head> — здесь только подхватываем
  React.useEffect(() => {
    const applied = document.documentElement.dataset.palette
    if (isPaletteId(applied)) setCurrent(applied)
  }, [])

  const apply = (id: PaletteId) => {
    document.documentElement.dataset.palette = id
    setCurrent(id)
    setOpen(false)
    try {
      window.localStorage.setItem(PALETTE_STORAGE_KEY, id)
    } catch {
      // приватный режим — переживём, палитра просто не запомнится
    }
    const url = new URL(window.location.href)
    if (id === DEFAULT_PALETTE) url.searchParams.delete('palette')
    else url.searchParams.set('palette', id)
    window.history.replaceState(null, '', url.toString())
  }

  const active = PALETTES.find((p) => p.id === current) ?? PALETTES[0]!

  return (
    <div className="fixed right-4 bottom-4 z-50 print:hidden">
      {open && (
        <div className="mb-3 w-72 max-w-[calc(100vw-2rem)] rounded-2xl border border-ink/15 bg-sand p-3 shadow-[0_12px_40px_rgba(0,0,0,0.25)]">
          <p className="px-1 pb-2 font-display text-xs uppercase tracking-wide text-ink/60">Цветовое решение</p>
          <ul className="flex flex-col gap-1">
            {PALETTES.map((p) => (
              <li key={p.id}>
                <button
                  className={cn(
                    'flex w-full items-center gap-3 rounded-xl px-2 py-2 text-left transition-colors',
                    p.id === current ? 'bg-ink/10' : 'hover:bg-ink/5',
                  )}
                  onClick={() => apply(p.id)}
                  type="button"
                >
                  <span className="flex shrink-0 overflow-hidden rounded-full border border-ink/20">
                    {p.swatch.map((c) => (
                      <span key={c} className="block size-5" style={{ backgroundColor: c }} />
                    ))}
                  </span>
                  <span className="min-w-0">
                    <span className="block font-display text-sm text-ink">{p.name}</span>
                    <span className="block text-xs text-ink/60">{p.note}</span>
                  </span>
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}

      <button
        aria-expanded={open}
        className="flex items-center gap-2 rounded-full border border-ink/20 bg-sun px-4 py-2.5 font-display text-sm text-ink shadow-[0_8px_24px_rgba(0,0,0,0.25)]"
        onClick={() => setOpen((v) => !v)}
        type="button"
      >
        <span className="flex overflow-hidden rounded-full border border-ink/20">
          {active.swatch.map((c) => (
            <span key={c} className="block size-3.5" style={{ backgroundColor: c }} />
          ))}
        </span>
        Палитра: {active.name}
      </button>
    </div>
  )
}
